import { makeStyles, MessageBar, MessageBarBody, tokens } from '@fluentui/react-components';
import { QueryAggregate, type ColumnAggregateInfo } from '@powerportalspro/core';
import { AuthStatus, useAuth, useT } from '@powerportalspro/react';
import { KanbanBoard, type ViewDefinition } from '@powerportalspro/react-fluent';
import { useMemo } from 'react';
import { DemoShowcaseHeading, DemoShowcaseLayout } from './DemoShowcaseLayout';
import { companiesIn, MIDWEST, NORTHEAST, SOUTHEAST, SOUTHWEST, WEST, type DemoRegion } from './DemoRegion';

// The open opportunities of the demo's sample data, a lane for each rating, with each lane's
// estimated revenue in its header, beside what to try on it. Cards are put in order by hand through
// a rank column of the demo's own, and a view for each region of the country narrows the board to the
// opportunities of companies there. The sample opportunities stay where they are: visitors can't
// change them. A signed-in visitor adds opportunities of their own with the + in a lane's header,
// and moves them for real.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.KanbanBoardShowcase.KanbanBoardShowcaseMainDemo';

// Dataverse's own "Open Opportunities" view, which every environment has under this id: the open
// sample opportunities, and a signed-in visitor's own.
const OPEN_OPPORTUNITIES_VIEW_ID = '00000000-0000-0000-00aa-000010003001';

// A view of the open opportunities of each region's companies, beside Dataverse's view of them all.
const REGION_VIEWS: { region: DemoRegion; viewId: string }[] = [
  { region: WEST, viewId: 'bfcad038-b94a-4e89-a18b-8dcd5f23de84' },
  { region: SOUTHWEST, viewId: 'e282f934-6f69-475e-a27c-4c8e50228a6a' },
  { region: MIDWEST, viewId: '531b1ae4-91af-4fc3-9387-60a927a5f4db' },
  { region: SOUTHEAST, viewId: '935e8585-49f1-4535-b71f-c386aa2e5894' },
  { region: NORTHEAST, viewId: '6faa0289-d2c0-4fda-bd90-b3d21d0b6a6e' },
];

// Dataverse's "Open Opportunities" of the region's companies alone: its columns, but for the email of a
// customer that is a contact, and its order. The lanes' totals are the board's.
function openOpportunitiesIn(id: string, displayName: string, region: DemoRegion): ViewDefinition {
  return {
    id,
    tableName: 'opportunity',
    displayName,
    fetchXml: `
      <fetch>
        <entity name="opportunity">
          <attribute name="name" />
          <attribute name="customerid" />
          <attribute name="estimatedclosedate" />
          <attribute name="estimatedvalue" />
          <attribute name="parentcontactid" />
          <attribute name="parentaccountid" />
          <attribute name="closeprobability" />
          <attribute name="opportunityratingcode" />
          <order attribute="estimatedclosedate" />
          <filter>
            <condition attribute="statecode" operator="eq" value="0" />
          </filter>
          ${companiesIn(region, 'parentaccountid')}
        </entity>
      </fetch>`,
    columns: [
      { columnName: 'name' },
      { columnName: 'customerid' },
      { columnName: 'estimatedclosedate' },
      { columnName: 'estimatedvalue' },
      { columnName: 'parentcontactid' },
      { columnName: 'parentaccountid' },
      { columnName: 'closeprobability' },
      { columnName: 'opportunityratingcode' },
    ],
  };
}

// Hot, Warm and Cold, without the lane of opportunities that have no rating.
const RATINGS = ['1', '2', '3'];

const TOTALS: ColumnAggregateInfo[] = [{ columnName: 'estimatedvalue', aggregates: [{ aggregate: QueryAggregate.Sum }] }];
const FORMATS = { estimatedvalue: 'C0' };

const useStyles = makeStyles({
  list: {
    margin: 0,
    paddingInlineStart: tokens.spacingHorizontalXL,
  },
});

// The strings are HTML, written once for both stacks.
function Item({ text }: { text: string }) {
  return <li dangerouslySetInnerHTML={{ __html: text }} />;
}

export function KanbanBoardShowcaseMainSample() {
  const styles = useStyles();
  const t = useT();
  const auth = useAuth();
  const signedIn = auth.status === AuthStatus.Authenticated;

  // Built once, as a new list starts the board again.
  const views = useMemo(
    () => REGION_VIEWS.map(({ region, viewId }) => openOpportunitiesIn(viewId, t(`${STRINGS_BASE}.view-${region.key}`), region)),
    [t],
  );

  return (
    <DemoShowcaseLayout
      title={t(`${STRINGS_BASE}.try-title`)}
      tips={
        <>
          <MessageBar intent="info">
            <MessageBarBody>{t(`${STRINGS_BASE}.${signedIn ? 'moves-saved' : 'sign-in-to-add'}`)}</MessageBarBody>
          </MessageBar>

          <ul className={styles.list}>
            <Item text={t(`${STRINGS_BASE}.try-totals`)} />
            <Item text={t(`${STRINGS_BASE}.try-add`)} />
            <Item text={t(`${STRINGS_BASE}.try-move`)} />
            <Item text={t(`${STRINGS_BASE}.try-order`)} />
            <Item text={t(`${STRINGS_BASE}.try-keyboard`)} />
            <Item text={t(`${STRINGS_BASE}.try-open`)} />
            <Item text={t(`${STRINGS_BASE}.try-lanes`)} />
            <Item text={t(`${STRINGS_BASE}.try-regions`)} />
            <Item text={t(`${STRINGS_BASE}.try-filter`)} />
            <Item text={t(`${STRINGS_BASE}.try-settings`)} />
            <Item text={t(`${STRINGS_BASE}.try-saved-views`)} />
            <Item text={t(`${STRINGS_BASE}.try-link`)} />
          </ul>

          <DemoShowcaseHeading>{t(`${STRINGS_BASE}.rules-title`)}</DemoShowcaseHeading>
          <ul className={styles.list}>
            <Item text={t(`${STRINGS_BASE}.rule-access`)} />
            <Item text={t(`${STRINGS_BASE}.rule-lanes`)} />
          </ul>
        </>
      }
    >
      <KanbanBoard
        defaultViewId={OPEN_OPPORTUNITIES_VIEW_ID}
        customViewDefinitions={views}
        laneColumn="opportunityratingcode"
        lanes={RATINGS}
        rankColumn="ppp_kanbanrank"
        laneAggregates={TOTALS}
        laneAggregateFormats={FORMATS}
        editable
        allowFiltering
        allowSavedViews
        savedViewsKey="kanban-board-showcase"
        persistedStateQueryParameter="boardState"
        height="760px"
      />
    </DemoShowcaseLayout>
  );
}
